/**
 * Group tenant KPIs by tag for the side-panel picker.
 * A KPI with several tags appears in each of those groups.
 * Untagged KPIs sit in one group at the end.
 */

import type { CatalogKpi } from './types';

export const UNTAGGED_TAG_KEY = '__untagged__';

/** C-level tags stay first; remaining tags sort A–Z. */
const PRIORITY_TAGS = ['ceo', 'cfo', 'coo', 'cio'];

export interface TagKpiGroup<T extends { kpiId: string; tags?: string[] } = CatalogKpi> {
  key: string;
  tag: string;
  items: T[];
}

export function groupCatalogByTag<T extends { kpiId: string; kpiName?: string; tags?: string[] }>(
  items: T[]
): TagKpiGroup<T>[] {
  const groups = new Map<string, TagKpiGroup<T>>();

  const add = (key: string, tag: string, item: T) => {
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { key, tag, items: [item] });
      return;
    }
    if (existing.items.some((row) => row.kpiId === item.kpiId)) return;
    existing.items.push(item);
  };

  for (const item of items) {
    const tags = (item.tags || []).map((tag) => String(tag || '').trim()).filter(Boolean);
    if (tags.length === 0) {
      add(UNTAGGED_TAG_KEY, UNTAGGED_TAG_KEY, item);
      continue;
    }
    for (const tag of tags) {
      add(tag.toLowerCase(), tag, item);
    }
  }

  const sorted = Array.from(groups.values()).sort((left, right) => {
    if (left.key === UNTAGGED_TAG_KEY) return 1;
    if (right.key === UNTAGGED_TAG_KEY) return -1;
    const leftRank = PRIORITY_TAGS.indexOf(left.key);
    const rightRank = PRIORITY_TAGS.indexOf(right.key);
    if (leftRank !== -1 || rightRank !== -1) {
      return (leftRank === -1 ? PRIORITY_TAGS.length : leftRank) - (rightRank === -1 ? PRIORITY_TAGS.length : rightRank);
    }
    return left.tag.localeCompare(right.tag, undefined, { sensitivity: 'base' });
  });

  for (const group of sorted) {
    group.items.sort((left, right) =>
      String(left.kpiName || '').localeCompare(String(right.kpiName || ''), undefined, { sensitivity: 'base' })
    );
  }
  return sorted;
}
